/**
 * Dialog Activity Context
 * STORY-003: Automatic page refresh fix
 *
 * Tracks when dialogs/modals are open to prevent disruptive background operations
 * like API polling from causing state updates that would close dialogs and lose user work.
 *
 * @example
 * ```tsx
 * // In a modal component
 * const { registerDialog, unregisterDialog } = useDialogActivity();
 *
 * useEffect(() => {
 *   registerDialog('my-modal');
 *   return () => unregisterDialog('my-modal');
 * }, []);
 *
 * // In a polling component
 * const { hasOpenDialogs } = useDialogActivity();
 *
 * useEffect(() => {
 *   if (hasOpenDialogs) return; // Skip polling while dialogs are open
 *   // ... polling logic
 * }, [hasOpenDialogs]);
 * ```
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';

/**
 * Dialog activity context state
 */
export interface DialogActivityContextState {
  /** Whether any dialogs are currently open */
  hasOpenDialogs: boolean;
  /** Number of currently open dialogs */
  openDialogCount: number;
  /** List of open dialog IDs (for debugging) */
  openDialogIds: string[];
  /** Register a dialog as open */
  registerDialog: (dialogId: string) => void;
  /** Unregister a dialog as closed */
  unregisterDialog: (dialogId: string) => void;
}

/**
 * Default context value
 */
const defaultContextValue: DialogActivityContextState = {
  hasOpenDialogs: false,
  openDialogCount: 0,
  openDialogIds: [],
  registerDialog: () => {
    console.warn('DialogActivityContext not initialized');
  },
  unregisterDialog: () => {
    console.warn('DialogActivityContext not initialized');
  },
};

/**
 * Dialog Activity Context
 */
export const DialogActivityContext = createContext<DialogActivityContextState>(defaultContextValue);

/**
 * Dialog Activity Provider Props
 */
export interface DialogActivityProviderProps {
  children: React.ReactNode;
}

/**
 * Dialog Activity Provider Component
 *
 * Wraps the application to track dialog/modal open state.
 * Components can register themselves when they open and unregister when they close.
 */
export const DialogActivityProvider: React.FC<DialogActivityProviderProps> = ({ children }) => {
  const [openDialogs, setOpenDialogs] = useState<Set<string>>(new Set());

  /**
   * Register a dialog as open
   */
  const registerDialog = useCallback((dialogId: string) => {
    setOpenDialogs((prev) => {
      const next = new Set(prev);
      next.add(dialogId);
      return next;
    });
  }, []);

  /**
   * Unregister a dialog as closed
   */
  const unregisterDialog = useCallback((dialogId: string) => {
    setOpenDialogs((prev) => {
      const next = new Set(prev);
      next.delete(dialogId);
      return next;
    });
  }, []);

  /**
   * Memoized context value
   */
  const contextValue = useMemo<DialogActivityContextState>(
    () => ({
      hasOpenDialogs: openDialogs.size > 0,
      openDialogCount: openDialogs.size,
      openDialogIds: Array.from(openDialogs),
      registerDialog,
      unregisterDialog,
    }),
    [openDialogs, registerDialog, unregisterDialog]
  );

  return (
    <DialogActivityContext.Provider value={contextValue}>
      {children}
    </DialogActivityContext.Provider>
  );
};

/**
 * Hook to access dialog activity context
 */
export function useDialogActivity(): DialogActivityContextState {
  const context = useContext(DialogActivityContext);
  return context;
}

/**
 * Hook that returns true if any dialog is open
 *
 * @returns boolean indicating if any dialog is currently open
 */
export function useHasOpenDialogs(): boolean {
  const { hasOpenDialogs } = useDialogActivity();
  return hasOpenDialogs;
}

export default DialogActivityContext;
